import { useEffect, useRef, useState } from 'react';

import { isEmpty } from 'lodash';

import { apiEndpoints, queryKeys, useDeleteMutation, useGetQuery, useUpdateMutation } from '#/services';
import { performStreamingRequest } from '#/services/apiClient';
import { CHATBOT_ROLE } from '#/utils/constants';
import {
  CHATBOT_SERVICE_UNAVAILABLE,
  CHATBOT_SERVICE_UNAVAILABLE_ERROR_MESSAGE,
  FAILED_TO_GET_RESPONSE_FROM_CHATBOT,
  MESSAGE_COPIED,
  REACTION_FAILED,
} from '#/utils/messages';

export function useChatbotMessages(options = {}) {
  const { onMessageSendErrorHandler, onMessageSendSuccessHandler } = options;
  const contentRef = useRef(null);
  const faqContainerRef = useRef(null);
  const additionalNoteRef = useRef(null);
  const activeStreamRef = useRef(null);
  const autoScrollEnabledRef = useRef(true);
  const userMessageAddedRef = useRef(false);
  const streamingStartedRef = useRef(false);
  const streamingMessageRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isStreamingLoading, setIsStreamingLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [faqQuestionId, setFaqQuestionId] = useState('');
  const [faqExpanded, setFaqExpanded] = useState(false);

  const queryParams = new URLSearchParams(window.location.search);

  const chatbotId = queryParams.get('id');
  const sessionId = queryParams.get('sessionId');
  const chatbotDefaultPosition = queryParams.get('position');

  const {
    data: chatbotData,
    isFetching,
    isPending,
  } = useGetQuery({
    key: queryKeys.CHATBOT_DETAIL,
    url: apiEndpoints.GET_CHATBOT_DETAILS(chatbotId),
    options: { retry: false, refetchOnWindowFocus: false, enabled: Boolean(chatbotId) },
  });

  const { data: reactions } = useGetQuery({
    key: queryKeys.REACTIONS,
    url: apiEndpoints.REACTIONS,
    options: { retry: false, refetchOnWindowFocus: false, enabled: Boolean(chatbotId) },
  });

  const {
    data: conversationHistory,
    refetch,
    isFetching: isConversationHistoryFetching,
  } = useGetQuery({
    key: queryKeys.CONVERSATION_HISTORY_KEY,
    url: `${apiEndpoints.CHATBOT_CONVERSATION}${sessionId}/`,
    params: { uuid: chatbotId },
    options: { retry: false, refetchOnWindowFocus: false, enabled: Boolean(chatbotId) },
  });

  const handleStreamingChunk = chunk => {
    if (!chunk) return;

    setStreamingMessage(prev => {
      const newMessage = prev + chunk;
      if (prev === '' && chunk) {
        setIsStreamingLoading(false);
        setIsStreaming(true);
        streamingStartedRef.current = true;

        // Scroll to make new streaming message visible at top of viewport (only once at start)
        if (autoScrollEnabledRef.current && contentRef.current) {
          // Use double requestAnimationFrame to ensure DOM is updated and streaming message is rendered
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (contentRef.current && streamingMessageRef.current) {
                const container = contentRef.current;
                const streamingMessageElement = streamingMessageRef.current;

                // Calculate scroll position to bring the streaming message to top of viewport
                const messageTop = streamingMessageElement.offsetTop;

                container.scrollTop = messageTop;
              }
            });
          });
        }
      }
      return newMessage;
    });
  };

  const handleStreamingComplete = fullResponse => {
    const assistantMessage = {
      text: fullResponse,
      created: new Date().toISOString(),
      role: CHATBOT_ROLE.ASSISTANT,
    };

    setMessages(prev => [...prev, assistantMessage]);
    setIsStreaming(false);
    setIsStreamingLoading(false);
    setStreamingMessage('');
    setIsSending(false);
    activeStreamRef.current = null;
    streamingStartedRef.current = false;
    userMessageAddedRef.current = false;
  };

  const handleStreamingSuccess = () => {
    setIsStreamingLoading(false);
    setIsStreaming(true);
    setIsSending(false);
    activeStreamRef.current = null;
    streamingStartedRef.current = false;
    userMessageAddedRef.current = false;
    refetch();
  };

  const handleStreamingError = error => {
    setIsStreamingLoading(false);
    setIsStreaming(false);
    setIsSending(false);
    setStreamingMessage('');
    activeStreamRef.current = null;
    streamingStartedRef.current = false;
    userMessageAddedRef.current = false;

    let errorMessage = error?.message || FAILED_TO_GET_RESPONSE_FROM_CHATBOT;

    if (errorMessage.includes(CHATBOT_SERVICE_UNAVAILABLE_ERROR_MESSAGE)) {
      errorMessage = CHATBOT_SERVICE_UNAVAILABLE;
    }

    onMessageSendErrorHandler(errorMessage);
  };

  const onReactSuccess = () => {
    refetch();
    setConversationId(null);
  };

  const onReactError = ({ response: { data } = {} }) => {
    const { detail, non_field_errors } = data || {};
    let message = detail || non_field_errors?.[0] || REACTION_FAILED;

    if (message.includes(CHATBOT_SERVICE_UNAVAILABLE_ERROR_MESSAGE)) {
      message = CHATBOT_SERVICE_UNAVAILABLE;
    }

    onMessageSendErrorHandler(message);
  };

  const { mutate: onEmojiReact } = useUpdateMutation({
    url: apiEndpoints.REACT_REACTION(conversationId),
    onSuccess: onReactSuccess,
    onError: onReactError,
  });

  const handleReaction = (id, text, emojiId, prevReaction) => () => {
    if (emojiId === 3) {
      navigator.clipboard.writeText(text).then(() => onMessageSendSuccessHandler(MESSAGE_COPIED));
    } else {
      setConversationId(id);
      onEmojiReact({
        payload: { reaction: emojiId === prevReaction ? null : emojiId },
      });
    }
  };

  const updateMessages = async (inputField, faqId) => {
    if (!inputField || !inputField.trim()) {
      return;
    }

    // Abort any existing stream before starting a new one
    if (activeStreamRef.current) {
      await activeStreamRef.current.abort();
      activeStreamRef.current = null;
    }

    const newMessage = {
      text: inputField,
      created: new Date().toISOString(),
      role: CHATBOT_ROLE.USER,
    };
    setIsSending(true);
    setIsStreamingLoading(true);
    setIsStreaming(false);
    setStreamingMessage('');

    setMessages(prev => [...prev, newMessage]);
    userMessageAddedRef.current = true;
    streamingStartedRef.current = false;

    if (faqId) {
      setFaqQuestionId(faqId);
    }

    try {
      const { abort } = performStreamingRequest({
        url: apiEndpoints.CHATBOT_STREAMING,
        payload: { query: inputField, chatbot_uuid: chatbotId, session_id: sessionId },
        onChunk: handleStreamingChunk,
        onComplete: handleStreamingComplete,
        onSuccess: handleStreamingSuccess,
        onError: handleStreamingError,
      });

      // Store the abort function for potential cancellation
      activeStreamRef.current = { abort };
    } catch (error) {
      handleStreamingError(error);
    }
  };

  const onResetChatSuccess = () => {
    refetch();
    setMessages([]);
    setFaqQuestionId('');
  };

  const {
    mutate: onResetChat,
    mutateAsync: onResetChatAsync,
    isPending: onResetChatLoading,
  } = useDeleteMutation({
    url: `${apiEndpoints.CHATBOT_CONVERSATION}${sessionId}/?uuid=${chatbotId}`,
    onSuccess: onResetChatSuccess,
  });

  const handleFaqStateChange = isExpanded => setFaqExpanded(isExpanded);

  useEffect(() => {
    if (!isEmpty(conversationHistory?.data?.results)) {
      setMessages(conversationHistory.data.results);
    }
  }, [conversationHistory]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === CHATBOT_ROLE.USER && contentRef.current && autoScrollEnabledRef.current) {
      requestAnimationFrame(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
      });
    }
  }, [messages]);

  // Handle scroll event to detect manual scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10;

      // If user scrolled up manually, disable auto-scroll
      if (!isAtBottom && scrollTop > 0) {
        autoScrollEnabledRef.current = false;
      }
      // If user scrolls back to bottom, re-enable auto-scroll
      else if (isAtBottom) {
        autoScrollEnabledRef.current = true;
      }
    };

    const scrollContainer = contentRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  useEffect(() => {
    return () => {
      // Abort any active stream on component unmount
      if (activeStreamRef.current) {
        activeStreamRef.current.abort();
        activeStreamRef.current = null;
      }
      setIsStreaming(false);
      setIsStreamingLoading(false);
      setStreamingMessage('');
      setIsSending(false);
      autoScrollEnabledRef.current = true;
      userMessageAddedRef.current = false;
      streamingStartedRef.current = false;
    };
  }, []);

  return {
    chatbotData,
    messages,
    isSending,
    isStreaming,
    isStreamingLoading,
    streamingMessage,
    updateMessages,
    handleReaction,
    contentRef,
    faqContainerRef,
    additionalNoteRef,
    streamingMessageRef,
    reactions,
    refetch,
    faqQuestionId,
    setFaqQuestionId,
    isFetching,
    isPending,
    chatbotDefaultPosition,
    faqExpanded,
    handleFaqStateChange,
    setMessages,
    onResetChat,
    onResetChatAsync,
    onResetChatLoading,
    isConversationHistoryFetching,
  };
}
